import { FlexBox } from "@joonapp/web-shared"
import { useEffect } from "react"
import { useShallow } from "zustand/react/shallow"

import GetStartedMobileBanner from "./components/getStarted/GetStartedMobileBanner"
import CreatingAccountPageLoader from "./components/loading/CreatingAccountPageLoader"
import MobileNav from "./components/mobileNav/MobileNav"
import ModalProvider from "./components/modals/ModalProvider"
import Sidebar from "./components/sidebar"
import { CustomToaster } from "./components/toaster/Toaster"
import { useAnalytics } from "./hooks/useAnalytics"
import useCoachingBundleVariantFlag from "./hooks/useCoachingBundleVariantFlag"
import useCustomOnboardParam from "./hooks/useCustomOnboardParam"
import useIsCoachingAvailable from "./hooks/useIsCoachingAvailable"
import useMobile from "./hooks/useMobile"
import useOnboardContext from "./hooks/useOnboardContext"
import { useOnboardingCustomPlanExperiment } from "./hooks/useOnboardingCustomPlanExperiment"
import { usePaywall } from "./hooks/usePaywall"
import { usePixelEvent } from "./hooks/usePixelEvent"
import { useShowWelcomeModal } from "./hooks/useShowWelcomeModal"
import { useUpdateSubscriberAttributes } from "./hooks/useUpdateSubscriberAttributes"
import {
  useFamilyQuery,
  useSubscriberInfo,
  useUserQuery,
} from "./networking/queries"
import NotFoundPage from "./pages/404"
import AcademyPage from "./pages/academy/AcademyPage"
import TrainingOverview from "./pages/academy/training/TrainingPage"
import AuthPage from "./pages/auth"
import BillingPage from "./pages/billing/BillingPage"
import CarePage from "./pages/care/CarePage"
import CheckoutPage from "./pages/checkout"
import CoachPage from "./pages/coach/CoachPage"
import SessionDetailsPage from "./pages/coach/sessions/SessionDetailsPage"
import CareMarketingPage from "./pages/coaching/careMarketing/CareMarketingPage"
import CoachingPage from "./pages/coaching/Coaching"
import useSkipCoachingStep from "./pages/coaching/useSkipCoachingStep"
import CoachOnboarding from "./pages/coachOnboarding/CoachOnboarding"
import CoachWelcomePage from "./pages/coachOnboarding/CoachWelcome"
import FamilyPage from "./pages/family/FamilyPage"
import InvitePage from "./pages/invite/Invite"
import IOSCancelSubscription from "./pages/iOSCancelSubscription/IOSCancelSubscription"
import MePage from "./pages/me"
import usePrivacyPolicyUpdate from "./pages/me/privacyPolicyUpdate/usePrivacyPolicyUpdate"
import OnboardAccountPage from "./pages/onboard"
import Paywall from "./pages/paywall/Paywall"
import PaywallMOF from "./pages/paywall/PaywallMOF"
import PrePaywallQuestionnaire from "./pages/paywall/PrePaywallQuestionnaire"
import useDiscountPaywallRedirect from "./pages/paywall/useDailyPaywallRedirect"
import QuestBoard from "./pages/questBoard/QuestBoard"
import ReferralLandingPage from "./pages/referralLanding/ReferralLandingPage"
import AllRewardTransactions from "./pages/rewards/AllRewardTransactions"
import RewardLogDetailsPage from "./pages/rewards/redeem/RewardLogDetailsPage"
import RewardsPage from "./pages/rewards/RewardsPage"
import SettingsPage from "./pages/settings/settings"
import WelcomePage from "./pages/welcome/Welcome"
import {
  ANALYTIC_EVENTS,
  FacebookPixelEvent,
  GAEvent,
  PinterestPixelEvent,
  TiktokPixelEvent,
  trackAnalyticEvent,
  trackGAEvent,
} from "./util/analytics"
import { useOnboardingExperiments } from "./util/experiments"
import { Route, Switch, useLocation, useRouter } from "./util/router"
import { localStorageItems, sessionManager } from "./util/storage"
import { getCurrentSubscription } from "./util/util"

const Routing = () => {
  const { user } = useUserQuery()
  const isLoggedIn = !!user
  const location = useLocation()
  const isMobile = useMobile()
  const isOnPaywallOrCoachingPage =
    location.pathname?.includes("/paywall") ||
    location.pathname?.includes("/coaching")
  const displayMobileNav = isLoggedIn && isMobile && !isOnPaywallOrCoachingPage
  const displaySidebar = isLoggedIn && !isMobile && !isOnPaywallOrCoachingPage
  const router = useRouter()
  const { data: family } = useFamilyQuery()
  const { isSubmittingData } = useOnboardContext(
    useShallow((state) => ({ isSubmittingData: state.isSubmittingData }))
  )

  const { trackFBPixelEvent, trackTikTokPixelEvent, trackPinterestEvent } =
    usePixelEvent()
  const { data: subscriberInfo } = useSubscriberInfo()

  useAnalytics()
  usePaywall()
  useDiscountPaywallRedirect()
  useUpdateSubscriberAttributes()
  useOnboardingExperiments()
  useCoachingBundleVariantFlag()
  useShowWelcomeModal()
  const isCoachingAvailable = useIsCoachingAvailable()
  const skipCoachingStep = useSkipCoachingStep()
  usePrivacyPolicyUpdate()
  useOnboardingCustomPlanExperiment()
  useCustomOnboardParam()

  // If the user has just paid, fire purchase complete event and clear query params
  // We may want to split up trial started and purchase completed --
  // TODO: see if there is a parameter that indicates this is a trial
  // Probably don't need this in routing file, and can add to routing hook
  useEffect(() => {
    if (!user || !family || !subscriberInfo) return
    if (router.query.paid) {
      const ttPixelParams = {
        content_type: "product",
        content_id: "100",
        quantity: 1,
        price: "89.99",
        value: "89.99",
        currency: "USD",
      }

      const currentSubscription = getCurrentSubscription(subscriberInfo)
      const hasTrial = currentSubscription?.period_type === "trial"

      // Enhanced Conversions enrichment — `user` is guaranteed loaded by
      // the early-return guard at the top of this effect. We pass email +
      // first/last name so Google Ads can match the conversion to the
      // originating ad click via hashed-PII even when cookies are blocked.
      const enhancedUserData = {
        email: user.email,
        firstName: user.name,
        lastName: user.last_name,
      }

      if (hasTrial) {
        trackFBPixelEvent(FacebookPixelEvent.START_TRIAL)
        trackTikTokPixelEvent(TiktokPixelEvent.START_TRIAL, ttPixelParams)
        trackPinterestEvent(PinterestPixelEvent.START_TRIAL)
        trackGAEvent(
          GAEvent.START_TRIAL,
          {
            value: 35,
            currency: "USD",
            transaction_id: `${family.legacy_family_id ?? family.id}`,
          },
          enhancedUserData
        )
      } else {
        trackTikTokPixelEvent(
          TiktokPixelEvent.PURCHASE_SUBSCRIPTION,
          ttPixelParams
        )
      }

      trackAnalyticEvent(ANALYTIC_EVENTS.PURCHASE_COMPLETED, {
        priceId: router.query.priceId,
        tpk: router.query.tpk,
        campaign: router.query.campaign,
      })

      trackGAEvent(
        GAEvent.PURCHASE_COMPLETED,
        {
          value: 90.0,
          currency: "USD",
          transaction_id: `${family.legacy_family_id ?? family.id}`,
        },
        enhancedUserData
      )
      sessionManager.clearNeedsToSeeVerifyFreemiumPopup()

      const userCameFromPaywall = !!localStorage.getItem(
        localStorageItems.stripeFromPaywall
      )

      if (userCameFromPaywall) {
        // The post-purchase step is a coaching upsell. Users who can't buy a coach
        // skip just that step — the rest of onboarding still has to run, so hand off
        // to the same skip the coaching landing page uses.
        if (isCoachingAvailable) router.push("/coaching")
        else skipCoachingStep()
        localStorage.removeItem(localStorageItems.stripeFromPaywall)
      }
    }
    // If user cancels from stripe payment page
    else if (router.query.cancelledPayment) {
      trackAnalyticEvent(ANALYTIC_EVENTS.PURCHASE_CANCELLED, {
        priceId: router.query.priceId,
        tpk: router.query.tpk,
        campaign: router.query.campaign,
      })

      const userCameFromPaywall = !!localStorage.getItem(
        localStorageItems.stripeFromPaywall
      )

      if (userCameFromPaywall) {
        router.push("/paywall/trial")
        localStorage.removeItem(localStorageItems.stripeFromPaywall)
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, router, user, family, subscriberInfo])

  if (isSubmittingData) return <CreatingAccountPageLoader />

  return (
    <>
      <ModalProvider />
      <CustomToaster />
      <FlexBox
        wrap={false}
        align="unset"
        justify="unset"
        direction={displayMobileNav || !isLoggedIn ? "column" : "row"}
        style={{ minWidth: "100vw" }}
      >
        {displaySidebar && <Sidebar />}
        {displayMobileNav && <MobileNav />}
        <GetStartedMobileBanner />
        <div style={{ flexGrow: 1 }}>
          <Switch>
            <Route exact path="/" component={MePage} />
            <Route exact path="/auth/:type" component={AuthPage} />
            <Route exact path="/billing" component={BillingPage} />
            <Route exact path="/care" component={CarePage} />
            <Route
              exact
              path="/care/:careTeamUserId/academy"
              component={AcademyPage}
            />
            <Route
              exact
              path="/care/:careTeamUserId/academy/:id"
              component={TrainingOverview}
            />
            <Route exact path="/checkout" component={CheckoutPage} />
            <Route exact path="/coach/" component={CareMarketingPage} />
            <Route exact path="/coach/:careTeamUserId" component={CoachPage} />
            <Route
              exact
              path="/coach/:careTeamUserId/:coachTab"
              component={CoachPage}
            />
            <Route
              exact
              path="/coach/:careTeamUserId/:coachTab/:sessionId"
              component={SessionDetailsPage}
            />
            <Route exact path="/coach-onboarding" component={CoachOnboarding} />
            <Route exact path="/coach-welcome" component={CoachWelcomePage} />
            <Route exact path="/coaching" component={CoachingPage} />
            <Route exact path="/family" component={FamilyPage} />
            <Route exact path="/invite" component={InvitePage} />
            <Route exact path="/me" component={MePage} />
            <Route exact path="/onboard" component={OnboardAccountPage} />
            <Route exact path="/paywall" component={PaywallMOF} />
            <Route
              exact
              path="/paywall/questionnaire"
              component={PrePaywallQuestionnaire}
            />
            <Route exact path="/paywall/trial" component={Paywall} />
            <Route exact path="/quests" component={QuestBoard} />
            <Route
              exact
              path="/referral-landing"
              component={ReferralLandingPage}
            />
            <Route exact path="/rewards" component={RewardsPage} />
            <Route
              exact
              path="/rewards/details/:rewardId"
              component={RewardLogDetailsPage}
            />
            <Route
              exact
              path="/rewards/transactions"
              component={AllRewardTransactions}
            />
            <Route exact path="/settings" component={SettingsPage} />
            <Route
              exact
              path="/subscription-cancel"
              component={IOSCancelSubscription}
            />
            <Route exact path="/welcome" component={WelcomePage} />
            <Route exact path="/welcome-fb" component={WelcomePage} />
            <Route exact path="/welcome-tt" component={WelcomePage} />
            <Route component={NotFoundPage} />
          </Switch>
        </div>
      </FlexBox>
    </>
  )
}

export default Routing
